import React, { useState, useEffect } from 'react';
import './App.css';

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: 'Buying a Home',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon!'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          interest: 'Buying a Home',
          message: ''
        });
      } else {
        setFormStatus({
          type: 'error',
          message: data.error || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Failed to send message. Please try again later or contact us directly via WhatsApp.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.mobile-menu')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-slate-700/50">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo + Brand Name */}
            <button onClick={() => scrollToSection('home')} className="flex items-center gap-3">
              <img src="/logo.png" alt="HemaRealty" className="w-auto object-contain" style={{ height: '195px', maxWidth: '420px' }} />
              <span className="font-extrabold leading-tight text-left hidden sm:block" style={{ fontSize: '1.1rem' }}>
                <span style={{ color: '#B22222', letterSpacing: '0.1em', display: 'block' }}>HEMA</span>
                <span style={{ color: '#DAA520', letterSpacing: '0.1em', display: 'block' }}>REALTY SERVICES</span>
              </span>
            </button>

            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('home')} className="nav-link">Home</button>
              <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
              <button onClick={() => scrollToSection('sell')} className="nav-link">Homes to Sell</button>
              <button onClick={() => scrollToSection('rent')} className="nav-link">Homes to Rent</button>
              <button onClick={() => scrollToSection('faq')} className="nav-link">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="mobile-menu md:hidden mt-4 py-4 bg-slate-800/95 backdrop-blur-md rounded-lg border border-slate-700/50">
              <button onClick={() => scrollToSection('home')} className="block w-full px-4 py-2 text-left hover:bg-slate-700/50 transition-colors">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full px-4 py-2 text-left hover:bg-slate-700/50 transition-colors">About</button>
              <button onClick={() => scrollToSection('sell')} className="block w-full px-4 py-2 text-left hover:bg-slate-700/50 transition-colors">Homes to Sell</button>
              <button onClick={() => scrollToSection('rent')} className="block w-full px-4 py-2 text-left hover:bg-slate-700/50 transition-colors">Homes to Rent</button>
              <button onClick={() => scrollToSection('faq')} className="block w-full px-4 py-2 text-left hover:bg-slate-700/50 transition-colors">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full px-4 py-2 text-left hover:bg-slate-700/50 transition-colors">Contact</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(1.05) contrast(1.15) saturate(1.3)' }}
        >
          <source src="/hero-video.mp4" type="video/mp4" />
        </video>

        {/* Cinematic dark overlay — lets video show through vividly */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(10,20,50,0.45) 50%, rgba(0,0,0,0.6) 100%)' }}></div>

        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto" style={{ textShadow: '0 4px 16px rgba(0,0,0,0.9), 0 2px 6px rgba(0,0,0,0.8)' }}>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="text-white">Your Dream Home in Lekki</span>
            <span className="text-blue-400 block">Awaits with HemaRealty</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Discover exceptional properties in Lekki, Lagos, Nigeria tailored to your lifestyle. 
            From stunning homes for sale to premium rentals, we make finding your perfect space effortless.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => scrollToSection('sell')}
              className="premium-btn bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
            >
              Browse Homes for Sale
            </button>
            <button 
              onClick={() => scrollToSection('rent')}
              className="premium-btn bg-transparent border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105"
            >
              View Rentals
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-slate-800/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">About HemaRealty</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Your trusted partner in real estate, connecting people with their perfect homes for over a decade.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="glass-card p-8 rounded-2xl">
              <h3 className="text-2xl font-bold mb-4 text-blue-400">Our Mission</h3>
              <p className="text-slate-300 mb-6 leading-relaxed">
                At HemaRealty, we're committed to making your real estate journey seamless and rewarding. 
                Whether you're buying your first home, upgrading to a larger space, or finding the perfect rental, 
                our experienced team is here to guide you every step of the way with personalized service and expert market knowledge.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-400">15+</div>
                  <div className="text-sm text-slate-400">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-400">2,500+</div>
                  <div className="text-sm text-slate-400">Happy Clients</div>
                </div>
              </div>
            </div>
            
            <div 
              className="rounded-2xl h-96 bg-cover bg-center"
              style={{
                backgroundImage: 'url(/property4.jpg)'
              }}
            ></div>
          </div>
        </div>
      </section>

      {/* Homes to Sell Section */}
      <section id="sell" className="py-20 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">Homes for Sale</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Explore our curated selection of premium properties ready to become your new home.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Property 1 */}
            <div className="service-card group">
              <div 
                className="h-64 bg-cover bg-center rounded-lg mb-6"
                style={{
                  backgroundImage: 'url(/property1.jpg)'
                }}
              ></div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-xl font-bold text-blue-400">Modern Family Home</h3>
                <span className="text-2xl font-bold text-white">₦230M</span>
              </div>
              <p className="text-slate-400 mb-4 text-sm">Ikate, Lekki, Lagos</p>
              <p className="text-slate-300 mb-4">
                Beautiful 4-bedroom, 3-bath home with spacious backyard and modern amenities.
              </p>
              <ul className="text-sm text-slate-400 space-y-2">
                <li>• 4 Bedrooms, 3 Bathrooms</li>
                <li>• 2,800 sq ft</li>
                <li>• 2-Car Garage</li>
                <li>• Updated Kitchen</li>
              </ul>
            </div>

            {/* Property 2 */}
            <div className="service-card group">
              <div 
                className="h-64 bg-cover bg-center rounded-lg mb-6"
                style={{
                  backgroundImage: 'url(/property2.jpg)'
                }}
              ></div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-xl font-bold text-blue-400">Luxury Villa</h3>
                <span className="text-2xl font-bold text-white">₦180M</span>
              </div>
              <p className="text-slate-400 mb-4 text-sm">Lekki Phase 1, Lagos</p>
              <p className="text-slate-300 mb-4">
                Stunning 5-bedroom luxury villa with pool, panoramic views, and premium finishes.
              </p>
              <ul className="text-sm text-slate-400 space-y-2">
                <li>• 5 Bedrooms, 4 Bathrooms</li>
                <li>• 4,200 sq ft</li>
                <li>• Pool & Spa</li>
                <li>• Mountain Views</li>
              </ul>
            </div>

            {/* Property 3 */}
            <div className="service-card group">
              <div 
                className="h-64 bg-cover bg-center rounded-lg mb-6"
                style={{
                  backgroundImage: 'url(/property3.jpg)'
                }}
              ></div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-xl font-bold text-blue-400">Cozy Townhouse</h3>
                <span className="text-2xl font-bold text-white">₦310M</span>
              </div>
              <p className="text-slate-400 mb-4 text-sm">Admiralty Way, Lekki Phase 1</p>
              <p className="text-slate-300 mb-4">
                Perfect starter home with 3 bedrooms in a vibrant downtown neighborhood.
              </p>
              <ul className="text-sm text-slate-400 space-y-2">
                <li>• 3 Bedrooms, 2.5 Bathrooms</li>
                <li>• 1,850 sq ft</li>
                <li>• Patio & Garden</li>
                <li>• Walk to Shops</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Homes to Rent Section */}
      <section id="rent" className="py-20 bg-slate-800/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">Homes for Rent</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Find your ideal rental property from our extensive portfolio of quality homes.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Rental 1 */}
            <div className="service-card group">
              <div 
                className="h-64 bg-cover bg-center rounded-lg mb-6"
                style={{
                  backgroundImage: 'url(/property5.jpg)'
                }}
              ></div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-xl font-bold text-blue-400">Modern Apartment</h3>
                <span className="text-2xl font-bold text-white">₦4.5M/yr</span>
              </div>
              <p className="text-slate-400 mb-4 text-sm">Oniru Estate, Lekki</p>
              <p className="text-slate-300 mb-4">
                Modern 2-bedroom apartment in the heart of downtown with city views.
              </p>
              <ul className="text-sm text-slate-400 space-y-2">
                <li>• 2 Bedrooms, 2 Bathrooms</li>
                <li>• 1,200 sq ft</li>
                <li>• Gym & Pool Access</li>
                <li>• Pet Friendly</li>
              </ul>
            </div>

            {/* Rental 2 */}
            <div className="service-card group">
              <div 
                className="h-64 bg-cover bg-center rounded-lg mb-6"
                style={{
                  backgroundImage: 'url(/property6.jpg)'
                }}
              ></div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-xl font-bold text-blue-400">Family Home</h3>
                <span className="text-2xl font-bold text-white">₦8M/yr</span>
              </div>
              <p className="text-slate-400 mb-4 text-sm">Osapa London, Lekki</p>
              <p className="text-slate-300 mb-4">
                Spacious 4-bedroom house perfect for families, near top-rated schools.
              </p>
              <ul className="text-sm text-slate-400 space-y-2">
                <li>• 4 Bedrooms, 3 Bathrooms</li>
                <li>• 2,600 sq ft</li>
                <li>• Fenced Backyard</li>
                <li>• Near Schools</li>
              </ul>
            </div>

            {/* Rental 3 */}
            <div className="service-card group">
              <div 
                className="h-64 bg-cover bg-center rounded-lg mb-6"
                style={{
                  backgroundImage: 'url(/property7.jpg)'
                }}
              ></div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-xl font-bold text-blue-400">Studio Apartment</h3>
                <span className="text-2xl font-bold text-white">₦3M/yr</span>
              </div>
              <p className="text-slate-400 mb-4 text-sm">Victoria Island, Lagos</p>
              <p className="text-slate-300 mb-4">
                Stylish studio loft in trendy arts district, perfect for young professionals.
              </p>
              <ul className="text-sm text-slate-400 space-y-2">
                <li>• Open Floor Plan</li>
                <li>• 750 sq ft</li>
                <li>• High Ceilings</li>
                <li>• Hardwood Floors</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">Frequently Asked Questions</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Got questions? We've got answers. Find helpful information about buying, selling, and renting.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-4">
            {/* FAQ 1 */}
            <div className="glass-card rounded-xl overflow-hidden">
              <button
                onClick={() => toggleFaq(0)}
                className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-slate-700/30 transition-colors"
              >
                <span className="text-lg font-semibold text-white">How do I schedule a property viewing?</span>
                <svg
                  className={`w-6 h-6 text-blue-400 transition-transform ${openFaq === 0 ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openFaq === 0 && (
                <div className="px-8 pb-6 text-slate-300">
                  Simply contact us through the form below or call our office. Our team will arrange a convenient time 
                  for you to view the property. Virtual tours are also available for most listings.
                </div>
              )}
            </div>

            {/* FAQ 2 */}
            <div className="glass-card rounded-xl overflow-hidden">
              <button
                onClick={() => toggleFaq(1)}
                className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-slate-700/30 transition-colors"
              >
                <span className="text-lg font-semibold text-white">What documents do I need to rent a property?</span>
                <svg
                  className={`w-6 h-6 text-blue-400 transition-transform ${openFaq === 1 ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openFaq === 1 && (
                <div className="px-8 pb-6 text-slate-300">
                  Typically, you'll need: proof of income (pay stubs or bank statements), valid ID, rental history, 
                  and references. A credit check may also be required. We'll guide you through the entire process.
                </div>
              )}
            </div>

            {/* FAQ 3 */}
            <div className="glass-card rounded-xl overflow-hidden">
              <button
                onClick={() => toggleFaq(2)}
                className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-slate-700/30 transition-colors"
              >
                <span className="text-lg font-semibold text-white">How does the home buying process work?</span>
                <svg
                  className={`w-6 h-6 text-blue-400 transition-transform ${openFaq === 2 ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openFaq === 2 && (
                <div className="px-8 pb-6 text-slate-300">
                  Our process includes: initial consultation, mortgage pre-approval, property search, making an offer, 
                  home inspection, finalizing financing, and closing. We'll be with you every step of the way.
                </div>
              )}
            </div>

            {/* FAQ 4 */}
            <div className="glass-card rounded-xl overflow-hidden">
              <button
                onClick={() => toggleFaq(3)}
                className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-slate-700/30 transition-colors"
              >
                <span className="text-lg font-semibold text-white">Are pets allowed in rental properties?</span>
                <svg
                  className={`w-6 h-6 text-blue-400 transition-transform ${openFaq === 3 ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openFaq === 3 && (
                <div className="px-8 pb-6 text-slate-300">
                  Pet policies vary by property. Many of our rentals are pet-friendly, though some may require an 
                  additional deposit or monthly fee. Check individual listings or contact us for specific pet policies.
                </div>
              )}
            </div>

            {/* FAQ 5 */}
            <div className="glass-card rounded-xl overflow-hidden">
              <button
                onClick={() => toggleFaq(4)}
                className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-slate-700/30 transition-colors"
              >
                <span className="text-lg font-semibold text-white">What are your commission rates?</span>
                <svg
                  className={`w-6 h-6 text-blue-400 transition-transform ${openFaq === 4 ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openFaq === 4 && (
                <div className="px-8 pb-6 text-slate-300">
                  Our commission rates are competitive and transparent. They vary based on the property type and 
                  transaction details. Contact us for a personalized quote and to discuss how we can help you.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-800/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">Get In Touch</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Ready to find your dream home? Contact us today for a personalized consultation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="glass-card p-8 rounded-2xl">
              <h3 className="text-2xl font-bold mb-6 text-blue-400">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-white font-semibold">Phone / WhatsApp</div>
                    <a href="https://wa.me/2348107450564" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-blue-400 transition-colors">
                      +234 810 745 0564
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-white font-semibold">Email</div>
                    <div className="text-slate-300">info@HemaRealty.org</div>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-white font-semibold">Hours</div>
                    <div className="text-slate-300">Mon-Fri: 9AM-6PM<br />Sat-Sun: 10AM-4PM</div>
                  </div>
                </div>
              </div>
              
              <a 
                href="https://wa.me/2348107450564?text=Hello%20HemaRealty,%20I'm%20interested%20in%20your%20properties%20in%20Lekki" 
                target="_blank" 
                rel="noopener noreferrer"
                className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white px-6 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Chat on WhatsApp
              </a>
            </div>
            
            <div className="glass-card p-8 rounded-2xl">
              <h3 className="text-2xl font-bold mb-6 text-blue-400">Send Us a Message</h3>
              
              {formStatus.message && (
                <div className={`mb-6 p-4 rounded-lg ${formStatus.type === 'success' ? 'bg-green-500/20 border border-green-500 text-green-300' : 'bg-red-500/20 border border-red-500 text-red-300'}`}>
                  {formStatus.message}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-white font-semibold mb-2">Name*</label>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your Name"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-semibold mb-2">Email*</label>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="your@email.com"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-semibold mb-2">Phone (optional)</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+234 XXX XXX XXXX"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-semibold mb-2">I'm Interested In</label>
                  <select 
                    name="interest"
                    value={formData.interest}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option>Buying a Home</option>
                    <option>Renting a Property</option>
                    <option>Selling My Home</option>
                    <option>Property Management</option>
                    <option>General Inquiry</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-white font-semibold mb-2">Message*</label>
                  <textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    required
                    rows="4" 
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Tell us about your real estate needs..."
                  ></textarea>
                </div>
                
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 ${isSubmitting ? 'bg-slate-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/2348107450564?text=Hello%20HemaRealty,%20I'm%20interested%20in%20your%20properties%20in%20Lekki"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-110"
        aria-label="Chat on WhatsApp"
      >
        <svg className="w-9 h-9 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>

      {/* Footer */}
      <footer className="bg-slate-900 py-12 border-t border-slate-800">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-blue-400">HemaRealty</h3>
              <p className="text-slate-400 mb-4">
                Your trusted partner in finding the perfect home. Excellence in real estate since 2009.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4 text-white">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('about')} className="text-slate-400 hover:text-blue-400 transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection('sell')} className="text-slate-400 hover:text-blue-400 transition-colors">Homes for Sale</button></li>
                <li><button onClick={() => scrollToSection('rent')} className="text-slate-400 hover:text-blue-400 transition-colors">Homes for Rent</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="text-slate-400 hover:text-blue-400 transition-colors">FAQ</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4 text-white">Services</h4>
              <ul className="space-y-2 text-slate-400">
                <li>Property Sales</li>
                <li>Rental Services</li>
                <li>Property Management</li>
                <li>Investment Consulting</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4 text-white">Connect With Us</h4>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="#" className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
                <a href="https://instagram.com/hemarealty" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-400">
            <p>&copy; 2026 HemaRealty. All rights reserved. | Licensed Real Estate Agency</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;